import React, { useState, useEffect } from "react";
import PlantedAcresCard from "../plantedAcres/PlantedAcresCard";
import { Row, Button, Col } from "react-bootstrap";
import EmptyRecipeUsage from "./EmptyRecipeUsage";
import RecipeUsageCard from "./RecipeUsageCard";
import "styles/RecipeUsage.css";
import RecipeUsageForm from "./RecipeUsageForm";
import { useSelector, useDispatch } from "react-redux";
import RecipeUsageFilter from "./RecipeUsageFilter";
import RecipeUsageService from "services/RecipeUsageService";
import {
  CHEMICAL_FETCH_ERROR,
  RECIPE_FETCH_ERROR,
  RECIPE_USAGE_FETCH_ERROR,
} from "configurations/MessageConstants";
import { setRecipeUsages } from "reduxStore/slices/recipeUsageSlice";
import CardSkeleton from "components/common/CardSkeleton";
import { useNavigate, useLocation } from "react-router-dom";
import { setRecipes, setChemicals } from "reduxStore/slices/recipeSlice";
import RecipeService from "services/RecipeService";
import { DATE_FORMAT, MODE_TYPES } from "configurations/Constants";
import { format } from "date-fns";
import CustomPagination from "components/common/CustomPagination";

const RecipeUsage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedPlantedAcre = useSelector(
    (state) => state.plantedAcres.selectedPlantedAcre
  );
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const isSharedViewOnly = selectedSite.isShared && !selectedSite.editor;
  const { year } = useSelector((state) => state.plantedAcresFilter);
  const recipeUsages = useSelector((state) => state.recipeUsage.recipeUsages);
  const [showAddRecipeUsage, setShowAddRecipeUsage] = useState(false);
  const [showRecipeUsageFilter, setShowRecipeUsageFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fromMap, setFromMap] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null,
  });
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedRecipeUsages, setPaginatedRecipeUsages] = useState([]);

  useEffect(() => {
    if (location.state && location.state.fromMap) {
      setFromMap(true);
    }
    getRecipeUsageByPlantedAcresId();
    setCurrentPage(1);
  }, [dispatch, location, selectedRecipes, dateRange]);

  useEffect(() => {
    getRecipes();
    getChemicals();
  }, []);

  const getRecipeUsageByPlantedAcresId = async () => {
    try {
      setIsLoading(true);
      if (selectedPlantedAcre && selectedPlantedAcre.id) {
        let params = "";

        const formattedDateRange = {
          from: dateRange.from ? format(dateRange.from, DATE_FORMAT) : null,
          to: dateRange.to ? format(dateRange.to, DATE_FORMAT) : null,
        };
        if (selectedRecipes && selectedRecipes.length > 0) {
          const recipeIds = selectedRecipes
            .map((recipe) => recipe.value)
            .join(",");
          params += `recipeId=${recipeIds}`;
        }
        if (formattedDateRange.from && formattedDateRange.to) {
          params += params
            ? `&dateFrom=${formattedDateRange.from}&dateTo=${formattedDateRange.to}`
            : `dateFrom=${formattedDateRange.from}&dateTo=${formattedDateRange.to}`;
        }
        const response =
          await RecipeUsageService.getRecipeUsageByPlantedAcresId(
            selectedSite.id,
            selectedPlantedAcre.id,
            params
          );
        dispatch(setRecipeUsages(response.items));
      }
    } catch (error) {
      console.error(RECIPE_USAGE_FETCH_ERROR, error);
      dispatch(setRecipeUsages(null));
    } finally {
      setIsLoading(false);
    }
  };

  const getRecipes = async () => {
    try {
      const response = await RecipeService.getRecipes(`year=${year}`);
      dispatch(setRecipes(response.items));
    } catch (error) {
      console.error(RECIPE_FETCH_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const getChemicals = async () => {
    setIsLoading(true);
    try {
      const response = await RecipeService.getChemicals();
      dispatch(setChemicals(response.items));
    } catch (error) {
      console.error(CHEMICAL_FETCH_ERROR, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPlantedAcres = () => {
    if (fromMap) navigate("/sites", { state: { fromBack: true } });
    else
      navigate(`/sites/${selectedSite.id}/planted-acres`, {
        state: { fromBack: true },
      });
  };

  const handleFilterToggle = () => {
    setShowRecipeUsageFilter(!showRecipeUsageFilter);
  };

  const handleAddRecipeUsageClose = () => {
    setShowAddRecipeUsage(false);
  };

  const handleApplyFilters = (filters) => {
    setSelectedRecipes(filters.selectedRecipes);
    setDateRange(filters.dateRange);
    const hasFilters =
      filters.selectedRecipes.length > 0 ||
      filters.dateRange.from !== null ||
      filters.dateRange.to !== null;
    setIsFilterApplied(hasFilters);
  };
  const handleResetFilters = () => {
    if (!isFilterApplied) return false;
    setSelectedRecipes([]);
    setDateRange({ from: null, to: null });
    setIsFilterApplied(false);
    return true;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div>
      <div className="d-flex align-items-center gap-2">
        <Button
          variant="link"
          className="p-0 text-black"
          onClick={handleBackToPlantedAcres}
          aria-label="Back to Planted Acres"
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
        </Button>
        <h5 className="m-0">Planted Acres</h5>
        <Button
          variant="outline-dark"
          size="sm"
          className={`${
            isFilterApplied ? "filter-icon-active" : "filter-icon"
          } pl-3 position-absolute end-0 rounded-0`}
          onClick={handleFilterToggle}
          aria-label="Recipe Usage Filter"
          title="Recipe Usage Filter"
        >
          <i className="fa fa-filter" aria-hidden="true"></i>
        </Button>
      </div>
      <div className="my-3 header-card-shadow rounded">
        {selectedPlantedAcre && (
          <PlantedAcresCard
            type="title-planted-acres-card"
            plantedAcre={selectedPlantedAcre}
            handlePlantedAcreClick={() => {}}
          />
        )}
      </div>
      <Row></Row>
      {isLoading || !recipeUsages ? (
        <CardSkeleton numberOfCards={3} />
      ) : recipeUsages.length === 0 ? (
        <div className="my-5">
          <EmptyRecipeUsage
            onAddRecipeUsage={() => setShowAddRecipeUsage(true)}
          />
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center my-2">
            <h6 className="mt-2">Recipes</h6>
            {!isSharedViewOnly && (
              <Button
                variant="outline-dark"
                size="sm"
                className="rounded-pill"
                onClick={() => {
                  setShowAddRecipeUsage(true);
                }}
              >
                <i className="fa fa-circle-plus me-1"></i> Apply Recipes
              </Button>
            )}
          </div>
          <Row className="g-3">
            {(paginatedRecipeUsages.length > 0
              ? paginatedRecipeUsages
              : recipeUsages
            ).map((usage) => (
              <Col key={usage.id} sm={12} md={6} lg={6} xl={4}>
                <RecipeUsageCard
                  recipeUsage={{
                    ...usage,
                    totalAcres: selectedPlantedAcre.acres,
                  }}
                />
              </Col>
            ))}
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={Math.ceil(recipeUsages.length / 9)}
            data={recipeUsages}
            itemsPerPage={9}
            onPageChange={handlePageChange}
            onDataChange={setPaginatedRecipeUsages}
          />
        </>
      )}
      <RecipeUsageForm
        mode={MODE_TYPES.ADD}
        show={showAddRecipeUsage}
        onClose={handleAddRecipeUsageClose}
      />
      <RecipeUsageFilter
        show={showRecipeUsageFilter}
        handleClose={() => setShowRecipeUsageFilter(false)}
        selectedRecipes={selectedRecipes}
        dateRange={dateRange}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
    </div>
  );
};

export default RecipeUsage;
